import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import FileUploadContext from './FileUploadContext';
import { FileWithContent } from '../services/PresentationService';
import claudeService from '../services/ClaudeService';
import slidePlanService, { PresentationOutline } from '../services/SlidePlanService';
import SlidePlanEditor from './SlidePlanEditor';
import presentationService from '../services/PresentationService';

export interface SlideContent {
  title: string;
  content: string;
  html?: string;
}

interface SlideEditorProps {
  onGenerateSlides: (content: SlideContent, files: FileWithContent[]) => void;
  isLoading?: boolean;
  initialTitle?: string;
  initialContent?: string;
  initialContextFiles?: FileWithContent[];
  submitButtonText?: string;
  presentationId?: string;
}

const SlideEditor: React.FC<SlideEditorProps> = ({ 
  onGenerateSlides, 
  isLoading = false,
  initialTitle = '',
  initialContent = '',
  initialContextFiles = [],
  submitButtonText = 'Generate Slides',
  presentationId
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [contextFiles, setContextFiles] = useState<FileWithContent[]>(initialContextFiles);
  const [showContextUploader, setShowContextUploader] = useState(initialContextFiles.length > 0);
  const [animateIn, setAnimateIn] = useState(false);
  
  // New state variables for streaming
  const [isGenerating, setIsGenerating] = useState(false);
  const [thinkingContent, setThinkingContent] = useState('');
  const [showThinking, setShowThinking] = useState(false);
  const [partialResponse, setPartialResponse] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const thinkingRef = useRef<HTMLDivElement>(null);
  
  // For checking if initialProps changed for real with JSON.stringify
  const initialContextFilesRef = useRef(JSON.stringify(initialContextFiles));
  const initialTitleRef = useRef(initialTitle);
  const initialContentRef = useRef(initialContent);
  
  // Slide planning state variables
  const [isPlanningSlides, setIsPlanningSlides] = useState(false);
  const [slidePlan, setSlidePlan] = useState<PresentationOutline | null>(null);
  const [slidePlanLoading, setSlidePlanLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  useEffect(() => {
    // Trigger entrance animation after component mounts
    const timer = setTimeout(() => setAnimateIn(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll thinking content
  useEffect(() => {
    if (thinkingRef.current && showThinking) {
      thinkingRef.current.scrollTop = thinkingRef.current.scrollHeight;
    }
  }, [thinkingContent, showThinking]);

  // Update state if initialProps change (for instance when switching between presentations)
  // Only run when the JSON stringified values actually change
  useEffect(() => {
    const newContextFilesJSON = JSON.stringify(initialContextFiles);
    
    if (
      initialTitleRef.current !== initialTitle || 
      initialContentRef.current !== initialContent ||
      initialContextFilesRef.current !== newContextFilesJSON
    ) {
      // Only update if there are actual changes
      setTitle(initialTitle);
      setContent(initialContent);
      setContextFiles(initialContextFiles);
      setShowContextUploader(initialContextFiles.length > 0);
      
      // Update refs
      initialTitleRef.current = initialTitle;
      initialContentRef.current = initialContent;
      initialContextFilesRef.current = newContextFilesJSON;
    }
  }, [initialTitle, initialContent, initialContextFiles]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Reset all state
    setIsGenerating(true);
    setThinkingContent('');
    setShowThinking(true);
    setPartialResponse('');
    setGenerationProgress(0);
    
    try {
      // Calculate thinking stages based on token length
      const totalTokenEstimate = await claudeService.countTokens(content, contextFiles);
      const thinkingMaxTokens = Math.min(totalTokenEstimate * 1.5, 15000); // Limit to 15k tokens
      
      // Call Claude API with streaming
      const html = await claudeService.generateSlides(
        { title, content },
        contextFiles,
        {
          onThinking: (thinking) => {
            setThinkingContent(thinking);
            // Estimate progress based on thinking length vs expected max
            const thinkingProgress = Math.min(
              (thinking.length / (thinkingMaxTokens * 4)) * 50, // Thinking is ~50% of progress
              45 // Cap at 45% to allow response to complete the rest
            );
            setGenerationProgress(Math.floor(thinkingProgress));
          },
          onPartialResponse: (text) => {
            setPartialResponse(text);
            // Start from 50% (thinking complete) and go to 90%
            const responseProgress = 50 + (Math.min(text.length / 5000, 1) * 40);
            setGenerationProgress(Math.floor(responseProgress));
          },
          onComplete: (html) => {
            setGenerationProgress(100);
            // Cleanup after a delay
            setTimeout(() => {
              setIsGenerating(false);
              setShowThinking(false);
            }, 1000);
          },
          onError: (error) => {
            console.error('Error generating slides:', error);
            alert(`Error generating slides: ${error}`);
            setIsGenerating(false);
          }
        }
      );
      
      // Now that we have the HTML result, call the parent component's handler
      // Pass the generated HTML so the parent doesn't need to make another API call
      onGenerateSlides({ title, content, html }, contextFiles);
    } catch (error) {
      console.error('Error generating slides:', error);
      alert(`Error generating slides: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsGenerating(false);
    }
  };

  const handleFilesProcessed = (processedFiles: FileWithContent[]) => {
    setContextFiles(processedFiles);
  };

  const handleGenerateSlidePlan = async (e: React.MouseEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!title || !content) {
      setErrorMessage("Please provide both title and content for your presentation.");
      return;
    }

    setIsPlanningSlides(false); // Reset first to ensure clean state
    setSlidePlanLoading(true);
    setIsGenerating(true); // Show the streaming UI
    setShowThinking(true);
    setThinkingContent(''); // Clear previous thinking content
    setPartialResponse(''); // Clear previous partial response
    
    // Start with a small progress value for immediate feedback
    setGenerationProgress(5);
    
    const slideContent: SlideContent = {
      title,
      content
    };

    try {
      console.log('Starting slide plan generation with slidePlanService.generateSlidePlan');
      
      // Track when last update was made to avoid too frequent UI updates
      let lastUpdateTime = Date.now();
      const updateThrottleMs = 50; // Throttle updates for smoother rendering
      
      const outline = await slidePlanService.generateSlidePlan(
        slideContent,
        contextFiles,
        {
          onThinking: (thinking: string) => {
            // Throttle updates to prevent UI jank
            const now = Date.now();
            if (now - lastUpdateTime > updateThrottleMs) {
              setThinkingContent(thinking);
              
              // Estimate progress based on thinking length to provide visual feedback
              const thinkingLength = thinking.length;
              let progress = 5;
              
              if (thinkingLength > 100) progress = 15;
              if (thinkingLength > 300) progress = 25;
              if (thinkingLength > 600) progress = 40;
              if (thinkingLength > 1000) progress = 55;
              if (thinkingLength > 1500) progress = 65;
              if (thinkingLength > 2000) progress = 75;
              
              setGenerationProgress(progress);
              
              // Scroll thinking container to bottom
              if (thinkingRef.current) {
                thinkingRef.current.scrollTop = thinkingRef.current.scrollHeight;
              }
              
              lastUpdateTime = now;
            }
          },
          onToolCall: (toolCall: any) => {
            console.log('Tool call received in SlideEditor:', toolCall);
            
            if (toolCall?.name === 'create_slide_outline' && toolCall.input) {
              setGenerationProgress(90);
              console.log('Received slide plan from tool call:', toolCall.input);
              setSlidePlan(toolCall.input); // Set the plan as soon as we get it
              
              // Add visual feedback in the thinking content
              setThinkingContent(prev => 
                prev + "\n\n✓ Generated slide plan structure:\n```json\n" + 
                JSON.stringify(toolCall.input, null, 2) + 
                "\n```"
              );
              
              setPartialResponse(JSON.stringify(toolCall.input, null, 2));
            }
          },
          onError: (error) => {
            console.error('Slide plan generation error in SlideEditor:', error);
            setErrorMessage(`Error generating slide plan: ${error}`);
            setIsGenerating(false);
            setSlidePlanLoading(false);
          },
          onComplete: (completedOutline) => {
            console.log('Slide plan generation completed in SlideEditor:', completedOutline);
            
            setGenerationProgress(100);
            console.log('Slide plan generation completed, preparing to show editor');
            setThinkingContent(prev => prev + "\n\n✅ Slide plan generation complete!");
            
            // Make sure we have a valid slide plan
            if (completedOutline && !slidePlan) {
              console.log('Setting slide plan from onComplete handler');
              setSlidePlan(completedOutline);
            }
            
            const finalOutline = completedOutline || slidePlan;
            
            // Save slide plan to Firestore if presentationId exists
            if (presentationId && finalOutline) {
              try {
                // Save the slide plan to the presentation
                presentationService.updateSlidePlan(presentationId, finalOutline)
                  .then(() => {
                    console.log('Slide plan saved to Firestore');
                  })
                  .catch(error => console.error('Error saving slide plan to Firestore:', error));
              } catch (error) {
                console.error('Error saving slide plan to Firestore:', error);
              }
            }
            
            if (!finalOutline) {
              console.error('No valid slide plan available after generation completed');
              setTimeout(() => {
                setSlidePlanLoading(false);
                setIsGenerating(false);
                setShowThinking(false);
              }, 2000);
              return;
            }
            
            // Short delay to show completion before switching to editor view
            setTimeout(() => {
              setIsGenerating(false);
              setShowThinking(false);
              setIsPlanningSlides(true);
              setSlidePlanLoading(false);
              console.log('SlidePlanEditor should now be visible', { 
                isPlanningSlides: true, 
                hasSlidePlan: !!slidePlan || !!completedOutline 
              });
            }, 1500);
          }
        }
      );
      
      // This might be reached if the outline is returned directly (not via callback)
      if (!slidePlan && outline) {
        console.log('Outline returned directly instead of through callbacks:', outline);
        setSlidePlan(outline);
        
        // Save the slide plan to Firestore
        if (presentationId) {
          try {
            await presentationService.updateSlidePlan(presentationId, outline);
            console.log('Slide plan saved to Firestore');
          } catch (saveError) {
            console.error('Error saving slide plan to Firestore:', saveError);
          }
        }
        
        setIsPlanningSlides(true);
      }
    } catch (error) {
      console.error("Error generating slide plan:", error);
      setIsGenerating(false);
      setSlidePlanLoading(false);
      setIsPlanningSlides(false);
      setErrorMessage("Failed to generate slide plan. Please try again.");
    }
  };

  const handleGenerateFromPlan = async (outline: PresentationOutline): Promise<string> => {
    setIsGenerating(true);
    setThinkingContent('Starting generation from plan...');
    setShowThinking(true);
    
    // Save the updated slide plan if we have a presentationId
    if (presentationId) {
      try {
        await presentationService.updateSlidePlan(presentationId, outline);
        console.log('Updated slide plan saved to Firestore');
      } catch (error) {
        console.error('Error saving updated slide plan to Firestore:', error);
        // Don't throw this error as it's not critical to the UX flow
      }
    }
    
    // Process slides to ensure template slides are properly handled
    const processedSlides = outline.slides.filter(slide => {
      // Filter out slides that only have template content
      return !(slide.title === 'New Slide' && slide.content === 'Enter content here...');
    }).map((slide, index) => {
      // Update slide numbers to ensure sequential ordering after filtering
      return {
        ...slide,
        slideNumber: index + 1,
        // Replace any instances of template content with more meaningful defaults
        content: slide.content === 'Enter content here...' 
          ? `Content for ${slide.title} slide` 
          : slide.content,
        title: slide.title === 'New Slide' 
          ? `Slide ${index + 1}` 
          : slide.title
      };
    });
    
    // Create a detailed slide content from the processed outline
    const enhancedContent: SlideContent = {
      title: outline.presentationTitle,
      content: `${outline.presentationSummary}\n\n${processedSlides.map(slide => 
        `Slide ${slide.slideNumber} (${slide.slideType}): ${slide.title}\n${slide.content}\n${slide.notes ? `Notes: ${slide.notes}` : ''}`
      ).join('\n\n')}`
    };
    
    console.log('Generating slides with content:', enhancedContent.content);
    
    try {
      const html = await claudeService.generateSlides(
        enhancedContent,
        contextFiles,
        {
          onThinking: (thinking) => {
            setThinkingContent(`Thinking: ${thinking.substring(thinking.length - 100)}`);
          },
          onPartialResponse: (text) => {
            setThinkingContent(`Generating slides${'.'.repeat(Math.floor(text.length / 100) % 4)}`);
            setPartialResponse(text);
          },
          onComplete: (html) => {
            // Notify parent component
            onGenerateSlides({
              title: enhancedContent.title,
              content: enhancedContent.content,
              html
            }, contextFiles);
            
            setIsPlanningSlides(false);
            setIsGenerating(false);
            setShowThinking(false);
          }
        }
      );
      
      return html;
    } catch (error) {
      console.error('Error generating slides from plan:', error);
      setThinkingContent('Error generating slides from plan');
      setShowThinking(false);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{size: []}],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, 
       {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  return (
    <div className={`bg-gray-100 dark:bg-gray-950 rounded-xl shadow-2xl overflow-hidden max-w-5xl mx-auto border border-gray-200 dark:border-gray-800 transition-all duration-1000 ease-out ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/10 via-gray-900/5 to-indigo-900/10"></div>
        <div className="relative bg-gradient-to-r from-gray-900 to-blue-950 p-8 text-white">
          <div className="absolute top-0 right-0 w-full h-full">
            <div className="absolute right-[-5%] top-[-50%] w-[40%] aspect-square rounded-full bg-blue-400/5 blur-3xl"></div>
            <div className="absolute right-[10%] top-[20%] w-[20%] aspect-square rounded-full bg-indigo-400/5 blur-3xl"></div>
          </div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center">
              <div className="mr-4 bg-white/5 p-3 rounded-lg backdrop-blur-sm border border-white/5 shadow-inner">
                <svg className="w-8 h-8 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-light tracking-tight text-gray-100">Executive Presentation Designer</h2>
                <p className="opacity-80 mt-1 text-gray-300 font-light">Create sophisticated slides with advanced AI technology</p>
              </div>
            </div>

            <div className="hidden md:flex space-x-3">
              <div className="text-xs uppercase tracking-wider text-gray-400 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 flex items-center">
                <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></span>
                Enterprise Ready
              </div>
              <div className="text-xs uppercase tracking-wider text-gray-400 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 flex items-center">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                AI Powered
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="p-8 bg-white dark:bg-gray-900">
        <div className="space-y-8">
          <div className="transition-all duration-500 ease-out transform" style={{transitionDelay: '100ms'}}>
            <label htmlFor="title" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center">
              <span className="w-5 h-5 inline-flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md mr-2 text-xs font-semibold">1</span>
              Presentation Title
            </label>
            <div className="relative group">
              <input
                type="text"
                id="title"
                name="presentation-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a descriptive title for your presentation"
                className="w-full px-5 py-3.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all shadow-sm premium-input"
                required
                autoComplete="off"
                aria-label="Presentation Title"
                data-form-type="other"
              />
            </div>
          </div>
          
          <div className="transition-all duration-500 ease-out transform" style={{transitionDelay: '200ms'}}>
            <label htmlFor="content" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center">
              <span className="w-5 h-5 inline-flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md mr-2 text-xs font-semibold">2</span>
              Presentation Content
            </label>
            <div className="relative">
              <div className="rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 shadow-sm border border-gray-300 dark:border-gray-700">
                <div className="premium-quill-container">
                  <ReactQuill
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    placeholder="Describe your presentation topic, main points to include, and any specific instructions..."
                    className="h-72 premium-editor"
                    theme="snow"
                  />
                </div>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-start">
              <svg className="w-4 h-4 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Provide details about your presentation's key points, target audience, and desired outcomes.
            </p>
          </div>
          
          <div className="transition-all duration-500 ease-out transform" style={{transitionDelay: '300ms'}}>
            <button
              type="button"
              onClick={() => setShowContextUploader(!showContextUploader)}
              className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium group"
            >
              <span className="w-6 h-6 inline-flex items-center justify-center rounded-full border border-blue-200 dark:border-blue-700 mr-2 group-hover:border-blue-300 dark:group-hover:border-blue-500 transition-all duration-300">
                <span className="text-xs">+</span>
              </span>
              <span className="mr-2">
                {contextFiles.length > 0 ? `Reference Materials (${contextFiles.length})` : 'Add Reference Materials'}
              </span>
              <svg 
                className={`w-5 h-5 transition-transform duration-300 ${showContextUploader ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div className={`transition-all duration-500 ease-out overflow-hidden ${showContextUploader ? 'max-h-[1000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
              <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-start mb-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-md border border-blue-100 dark:border-blue-800 mr-4 flex-shrink-0">
                    <svg className="w-6 h-6 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Upload Reference Materials</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Enhance your presentation with company reports, market analyses, financial data, or any relevant documents.
                      We support PDFs, spreadsheets, and other document formats.
                    </p>
                  </div>
                </div>
                <FileUploadContext 
                  onFilesProcessed={handleFilesProcessed} 
                  slideTitleContent={title}
                  slideBodyContent={content}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mt-12 transition-all duration-500 ease-out transform" style={{transitionDelay: '400ms'}}>
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full max-w-2xl">
            <button
              onClick={handleGenerateSlidePlan}
              disabled={!title || !content || slidePlanLoading}
              className={`
                px-6 py-3.5 rounded-lg font-medium text-white
                relative group overflow-hidden flex-1 premium-button
                ${(!title || !content || slidePlanLoading)
                  ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg premium-shadow'
                } 
                transition-all duration-300 
                flex items-center justify-center
              `}
              type="button"
            >
              {slidePlanLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Planning...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                  Create Presentation Outline
                </>
              )}
            </button>
          
            <button
              type="submit"
              disabled={isLoading || isGenerating}
              className={`
                px-9 py-3.5 rounded-lg font-medium
                relative group overflow-hidden flex-1 premium-button
                ${isLoading || isGenerating ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-700 to-indigo-800 text-white hover:from-blue-600 hover:to-indigo-700 shadow-md hover:shadow-lg premium-shadow'} 
                transition-all duration-300 transform hover:-translate-y-0.5 
                flex items-center justify-center
              `}
            >
              {isLoading || isGenerating ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-lg">Processing...</span>
                </div>
              ) : (
                <div className="flex items-center relative z-10">
                  <svg className="mr-2 w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                  <span>{submitButtonText}</span>
                </div>
              )}
              
              {/* Button shine effect */}
              <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shine" />
            </button>
          </div>
        </div>
        
        {/* Features section */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-lg border border-gray-100 dark:border-gray-800">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 mr-3 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 dark:text-white">Fast Execution</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Generate professional-quality presentations in minutes, not hours.</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-lg border border-gray-100 dark:border-gray-800">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 mr-3 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 dark:text-white">Enterprise Grade</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Built for consulting firms and financial institutions with security in mind.</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-lg border border-gray-100 dark:border-gray-800">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 mr-3 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 dark:text-white">Customizable</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Full control over slide structure, content, and design for your brand identity.</p>
            </div>
          </div>
        </div>
      </form>
      
      {/* Generation Progress UI */}
      {isGenerating && (
        <div className="fixed inset-0 bg-gray-900/80 z-50 overflow-y-auto backdrop-blur-sm flex items-center justify-center">
          <div className="w-full max-w-4xl mx-auto p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full mr-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    Creating Executive Presentation
                    {generationProgress >= 100 && (
                      <span className="ml-2 text-green-500 text-sm flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Complete
                      </span>
                    )}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    {generationProgress < 30 ? 'Analyzing content and structuring key insights...' :
                    generationProgress < 60 ? 'Developing presentation framework and narrative flow...' :
                    generationProgress < 90 ? 'Refining strategic messaging and data organization...' :
                    'Finalizing executive-ready presentation...'}
                  </p>
                </div>
              </div>
              
              {/* Progress bar with branded styling */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                  <span>{generationProgress < 100 ? 'Generating presentation...' : 'Presentation ready'}</span>
                  <span>{generationProgress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${generationProgress === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}
                    style={{ width: `${generationProgress}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Tabs for thinking vs final structure */}
              <div className="mb-3 border-b border-gray-200 dark:border-gray-800">
                <div className="flex">
                  <button 
                    onClick={() => setShowThinking(true)}
                    className={`px-4 py-2 text-sm font-medium ${
                      showThinking 
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Analysis Process
                  </button>
                  <button 
                    onClick={() => setShowThinking(false)}
                    className={`px-4 py-2 text-sm font-medium ${
                      !showThinking 
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                    disabled={!partialResponse}
                  >
                    Presentation Structure
                    {partialResponse && (
                      <span className="ml-2 inline-flex items-center justify-center w-2 h-2 bg-green-500 rounded-full"></span>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Thinking content */}
              {showThinking ? (
                <div ref={thinkingRef} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 h-[400px] overflow-y-auto premium-scrollbar">
                  <div className="flex items-center mb-2">
                    <div className={`w-4 h-4 ${generationProgress < 100 ? 'bg-blue-600 animate-pulse' : 'bg-green-500'} rounded-full mr-2`}></div>
                    <h4 className="text-blue-700 dark:text-blue-400 font-semibold">
                      {generationProgress < 100 ? 'AI Analysis in Progress' : 'Analysis Complete'}
                    </h4>
                  </div>
                  <div className="whitespace-pre-wrap font-mono text-xs text-gray-700 dark:text-gray-300 overflow-auto">
                    {thinkingContent || 'Initializing AI engine...\nAnalyzing content requirements and audience expectations...'}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 h-[400px] overflow-y-auto premium-scrollbar">
                  <div className="flex items-center mb-2">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                    <h4 className="text-blue-700 dark:text-blue-400 font-semibold">Presentation Framework</h4>
                  </div>
                  <pre className="whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-300 overflow-auto">
                    {partialResponse || 'Waiting for presentation framework to be generated...'}
                  </pre>
                </div>
              )}
              
              {/* Message about what's happening with business focus */}
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 flex items-center bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800/30">
                <svg className="w-5 h-5 mr-2 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>Our AI is creating a strategic presentation structure optimized for executive audiences. You'll be able to review and refine before finalizing your slides.</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom styling for the editor */}
      <style>{`
        .premium-quill-container {
          border-radius: 0.5rem;
          overflow: hidden;
        }
        
        .premium-editor .ql-toolbar {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 10px 15px;
          border-top: none;
          border-left: none;
          border-right: none;
        }
        
        .dark .premium-editor .ql-toolbar {
          background: #1e293b;
          border-bottom: 1px solid #334155;
        }
        
        .premium-editor .ql-container {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 1rem;
          border: none;
          background: #f8fafc;
        }
        
        .dark .premium-editor .ql-container {
          background: #1e293b;
        }
        
        .premium-editor .ql-editor {
          padding: 18px 20px;
          color: #334155;
          min-height: 270px;
        }
        
        .dark .premium-editor .ql-editor {
          color: #e2e8f0;
        }
        
        .premium-editor .ql-editor.ql-blank::before {
          color: #94a3b8;
          font-style: normal;
          left: 20px;
          right: 20px;
        }
        
        .dark .premium-editor .ql-editor.ql-blank::before {
          color: #64748b;
        }
        
        .premium-editor .ql-snow.ql-toolbar button, 
        .premium-editor .ql-snow .ql-toolbar button {
          color: #64748b;
        }
        
        .dark .premium-editor .ql-snow.ql-toolbar button, 
        .dark .premium-editor .ql-snow .ql-toolbar button {
          color: #94a3b8;
        }
        
        .premium-editor .ql-snow.ql-toolbar button:hover, 
        .premium-editor .ql-snow .ql-toolbar button:hover,
        .premium-editor .ql-snow.ql-toolbar button.ql-active,
        .premium-editor .ql-snow .ql-toolbar button.ql-active {
          color: #3b82f6;
        }
        
        .dark .premium-editor .ql-snow.ql-toolbar button:hover, 
        .dark .premium-editor .ql-snow .ql-toolbar button:hover,
        .dark .premium-editor .ql-snow.ql-toolbar button.ql-active,
        .dark .premium-editor .ql-snow .ql-toolbar button.ql-active {
          color: #60a5fa;
        }
        
        .premium-editor .ql-snow.ql-toolbar button:hover .ql-stroke, 
        .premium-editor .ql-snow .ql-toolbar button:hover .ql-stroke,
        .premium-editor .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .premium-editor .ql-snow .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3b82f6;
        }
        
        .dark .premium-editor .ql-snow.ql-toolbar button:hover .ql-stroke, 
        .dark .premium-editor .ql-snow .ql-toolbar button:hover .ql-stroke,
        .dark .premium-editor .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .dark .premium-editor .ql-snow .ql-toolbar button.ql-active .ql-stroke {
          stroke: #60a5fa;
        }
        
        .premium-editor .ql-snow.ql-toolbar button:hover .ql-fill, 
        .premium-editor .ql-snow .ql-toolbar button:hover .ql-fill,
        .premium-editor .ql-snow.ql-toolbar button.ql-active .ql-fill,
        .premium-editor .ql-snow .ql-toolbar button.ql-active .ql-fill {
          fill: #3b82f6;
        }
        
        .dark .premium-editor .ql-snow.ql-toolbar button:hover .ql-fill, 
        .dark .premium-editor .ql-snow .ql-toolbar button:hover .ql-fill,
        .dark .premium-editor .ql-snow.ql-toolbar button.ql-active .ql-fill,
        .dark .premium-editor .ql-snow .ql-toolbar button.ql-active .ql-fill {
          fill: #60a5fa;
        }
        
        .premium-editor .ql-snow .ql-stroke {
          stroke: #64748b;
        }
        
        .dark .premium-editor .ql-snow .ql-stroke {
          stroke: #94a3b8;
        }
        
        .premium-editor .ql-snow .ql-fill {
          fill: #64748b;
        }
        
        .dark .premium-editor .ql-snow .ql-fill {
          fill: #94a3b8;
        }
        
        .premium-editor .ql-snow .ql-picker {
          color: #64748b;
        }
        
        .dark .premium-editor .ql-snow .ql-picker {
          color: #94a3b8;
        }
        
        .premium-editor .ql-snow .ql-picker-options {
          background-color: white;
          border-color: #e2e8f0;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          padding: 8px;
          border-radius: 0.375rem;
        }
        
        .dark .premium-editor .ql-snow .ql-picker-options {
          background-color: #1e293b;
          border-color: #334155;
        }
        
        .premium-editor .ql-snow .ql-tooltip {
          background-color: white;
          border-color: #e2e8f0;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          color: #1e293b;
          border-radius: 0.375rem;
        }
        
        .dark .premium-editor .ql-snow .ql-tooltip {
          background-color: #1e293b;
          border-color: #334155;
          color: #f8fafc;
        }
        
        .premium-editor .ql-snow .ql-tooltip input[type=text] {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #1e293b;
          outline: none;
          border-radius: 0.25rem;
        }
        
        .dark .premium-editor .ql-snow .ql-tooltip input[type=text] {
          background: #0f172a;
          border: 1px solid #334155;
          color: #f8fafc;
        }
        
        .premium-editor .ql-snow a {
          color: #3b82f6;
        }
        
        .dark .premium-editor .ql-snow a {
          color: #60a5fa;
        }

        /* Scrollbar styles */
        .premium-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(148, 163, 184, 0.5) rgba(241, 245, 249, 0.5);
        }
        .premium-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .premium-scrollbar::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.5);
          border-radius: 3px;
        }
        .premium-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(148, 163, 184, 0.5);
          border-radius: 3px;
        }
        
        .dark .premium-scrollbar {
          scrollbar-color: rgba(71, 85, 105, 0.5) rgba(15, 23, 42, 0.5);
        }
        .dark .premium-scrollbar::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.5);
        }
        .dark .premium-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(71, 85, 105, 0.5);
        }
        
        /* Button shine animation */
        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        
        .animate-shine {
          animation: shine 1.2s linear infinite;
        }
        
        /* Premium UI elements */
        .premium-gradient {
          background-image: linear-gradient(135deg, #2563eb, #4f46e5, #6366f1);
        }
        
        .premium-shadow {
          box-shadow: 0 4px 14px -4px rgba(37, 99, 235, 0.3);
        }
        
        .dark .premium-shadow {
          box-shadow: 0 4px 14px -4px rgba(37, 99, 235, 0.5);
        }
        
        /* Input focus styles */
        .premium-input:focus {
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.5);
        }
        
        .dark .premium-input:focus {
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
          border-color: rgba(59, 130, 246, 0.5);
        }
        
        /* Card hover effect */
        .premium-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .premium-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 18px -5px rgba(37, 99, 235, 0.2);
        }
        
        .dark .premium-card:hover {
          box-shadow: 0 6px 18px -5px rgba(37, 99, 235, 0.3);
        }
        
        /* Button transitions */
        .premium-button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        
        .premium-button:after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent, 
            rgba(255, 255, 255, 0.1), 
            transparent
          );
          transition: 0.4s;
        }
        
        .premium-button:hover:after {
          left: 100%;
        }
        
        /* Corporate accent colors */
        .accent-navy {
          color: #1e3a8a;
        }
        
        .accent-gold {
          color: #b45309;
        }
        
        .bg-accent-navy {
          background-color: #1e3a8a;
        }
        
        .bg-accent-gold {
          background-color: #b45309;
        }
      `}</style>
      
      {/* SlidePlanEditor Modal */}
      {isPlanningSlides && slidePlan && (
        <div className="fixed inset-0 bg-gray-900/80 z-[60] overflow-y-auto flex items-center justify-center backdrop-blur-sm">
          <div className="w-full max-w-6xl mx-auto p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-800">
              <SlidePlanEditor
                outline={slidePlan}
                onSave={(outline) => {
                  setSlidePlan(outline);
                  // If we have a presentationId, save the updated outline to Firestore
                  if (presentationId) {
                    presentationService.updateSlidePlan(presentationId, outline)
                      .catch(error => console.error('Error saving slide plan update:', error));
                  }
                }}
                onGenerate={handleGenerateFromPlan}
                onCancel={() => setIsPlanningSlides(false)}
                isLoadedFromFirestore={false} // Always false for newly generated plans
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlideEditor; 